<!--
  //app/components/pages/region-squad-simulator/CardTeamSquadSimulator.vue
  -->
<script setup lang="ts">
import type { Team } from "~~/src/team/domain/models/Team";
import { useTeams } from "~/composables/teams";
import { useRegions } from "~/composables/regions";
import { setAliasUrl } from "~~/src/Shared/utils";
import { useImages } from "~/composables/images";

type Props = {
  team: Team;
  selectedTeam: Team | undefined;
  teamBudget: number | undefined;
  preload?: boolean;
  isDownload?: boolean;
  nationsSimulator?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits(["update:selectedTeam"]);

const { $t } = useI18n();
const { findTeamPresident } = useTeams();
const { isAmericas, isWorldCup } = useRegions();
const { playerPlaceholderPath } = useImages();

const defaultColor = "#888888";

const teamPresident = findTeamPresident(props.team);

const aliasUrlPresidentImage = teamPresident
  ? "/_ipx/fit_outside&s_220x140/" + setAliasUrl(teamPresident?.image?.url)
  : playerPlaceholderPath;
const aliasUrlTeamLogoImage = "/_ipx/s_100x100/" + setAliasUrl(props.team?.logo?.url);

const updateSelectedTeam = (team: Team) => {
  window.scroll(0, 0);
  emit("update:selectedTeam", team);
};

const firstColorHex = props.team.firstColorHEX ? props.team.firstColorHEX : defaultColor;
const secondColorHex = props.team.secondColorHEX ? props.team.secondColorHEX : defaultColor;
</script>

<template>
  <div
    :class="[
      'team-card-container',
      { 'hover:border hover:border-primary hover:cursor-pointer': !selectedTeam },
      {
        'col-start-auto md:col-start-2 md:col-span-3 xl:col-start-2 xl:col-span-1':
          !!selectedTeam && !nationsSimulator,
      },
      {
        'col-start-1 col-span-full md:col-start-1 md:col-span-3 xl:col-start-2 xl:col-span-1':
          !!selectedTeam && !!nationsSimulator,
      },
      { '!h-[130px]': nationsSimulator },
    ]"
    v-if="!selectedTeam || selectedTeam.id === team.id"
    @click="updateSelectedTeam(team)"
  >
    <div class="h-full w-full">
      <div class="flex justify-between items-center h-full relative z-[3]">
        <div
          class="clip-diagonal-right"
          :style="`background-image: linear-gradient(180deg, ${
            firstColorHex
          }99 30%, ${secondColorHex}99 90%);`"
        ></div>
        <div
          class="gradient-team-color-hover"
          :style="`background-image: linear-gradient(0deg, ${firstColorHex}10 0%, rgb(20,20,20) 100%);`"
        ></div>
        <div
          v-if="!isWorldCup"
          class="clip-diagonal-left"
          :style="`background-image: linear-gradient(0deg, ${firstColorHex}20 0%, ${secondColorHex}20 100%);`"
        ></div>
        <div
          v-else-if="isWorldCup"
          :class="['clip-diagonal-left', { 'opacity-30': team.metaInformation?.flag_team_url }]"
          :style="
            team.metaInformation?.flag_team_url
              ? `background-image: url('${team.metaInformation.flag_team_url}'); background-position: center center; background-size: cover;`
              : `background-image: linear-gradient(0deg, ${firstColorHex}20 0%, ${secondColorHex}20 100%);`
          "
        ></div>
      </div>

      <div class="team-card-content">
        <div class="container-logo-img">
          <NuxtImg
            v-if="!isDownload"
            :src="setAliasUrl(team.logo.url) || ''"
            :alt="team.completeName || ''"
            :preload="preload"
            class="object-contain"
            width="100"
            height="100"
            sizes="69px sm:100px"
          />
          <img v-else :src="aliasUrlTeamLogoImage" :alt="team.completeName || ''" width="100" height="100" />
        </div>

        <div
          :class="[
            'container-team-info',
            { '-ms-14 sm:-ms-2': !nationsSimulator },
            { '!justify-start -ms-5': nationsSimulator },
          ]"
        >
          <div v-if="!nationsSimulator" class="container-president-img">
            <NuxtImg
              v-if="!!teamPresident && !isDownload"
              :src="setAliasUrl(teamPresident?.image?.url) || playerPlaceholderPath"
              :alt="teamPresident.shortName || ''"
              :preload="preload"
              fit="outside"
              width="235"
              height="150"
              sizes="220px sm:235px"
              :class="[
                'max-h-[150px] object-top object-contain',
                !isAmericas ? 'max-w-[120%]' : 'max-w-full',
              ]"
            />
            <img
              v-else
              :src="aliasUrlPresidentImage || playerPlaceholderPath"
              :alt="teamPresident?.shortName || ''"
              width="235"
              height="150"
              class="max-h-[150px] object-contain object-top"
            />
          </div>

          <div :class="['container-team-name-text', { 'no-president': nationsSimulator }]">
            <h2 class="team-name">{{ team.completeName }}</h2>
            <p v-if="!nationsSimulator" class="team-president">
              {{ !!teamPresident ? teamPresident.shortName : "---" }}
            </p>

            <div v-if="teamBudget && !nationsSimulator" class="budget-label">
              {{ $t("team.budget.title") }} <span class="font-bold">{{ teamBudget }} M</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.team-card-container {
  @apply rounded-lg bg-gray-900 relative h-[172px] overflow-hidden;
}

.clip-diagonal-right {
  @apply h-full w-[133px] relative z-[3] rtl:-scale-x-100 rtl:scale-y-100;
  clip-path: polygon(0 0, 100% 0%, 35% 100%, 0% 100%);
}

.clip-diagonal-left {
  @apply h-full w-[73px] ltr:translate-x-2 rtl:-translate-x-2 bg-opacity-20 z-[2] rtl:-scale-x-100 rtl:scale-y-100;
  clip-path: polygon(100% 0, 100% 0%, 100% 100%, 0% 100%);
}

.team-card-content {
  @apply flex justify-start items-start h-full w-full absolute top-0 right-0 bottom-0 left-0 z-[5];
}

.container-logo-img {
  @apply flex justify-center items-center p-2 min-h-[85px] min-w-[85px] max-w-[85px] max-h-[85px] h-[85px] md:min-h-[100px] md:min-w-[100px] md:max-w-[100px] md:max-h-[100px] md:h-[100px];
}

.container-logo-img img,
.container-logo-img div {
  @apply transition-transform ease-in-out duration-300 origin-top-left;
}

.container-team-info {
  @apply flex justify-start gap-3 items-end h-full pt-4 sm:-ms-8 pe-2 sm:pe-4;
}

.container-president-img {
  @apply -ms-6 sm:ms-0 sm:shrink-0 max-w-[65%] sm:max-w-full;
}

.container-president-img img {
  @apply transition-transform ease-in-out duration-300 origin-bottom;
}

.container-team-name-text {
  @apply flex flex-col justify-center items-start h-full pb-4 md:-ms-8 w-[110px] sm:w-auto;
}

.container-team-name-text.no-president {
  @apply !ms-0 !flex-row !justify-start !items-center !min-w-full !w-auto px-16;
}

.team-name {
  @apply text-gray-300 text-base md:text-xl font-bold;
}

.team-president {
  @apply text-gray-400 text-sm leading-[21px];
}

.budget-label {
  @apply bg-primary text-black text-sm px-2.5 py-1.5 rounded-md mt-2;
}

.clip-diagonal-right,
.clip-diagonal-left,
.gradient-team-color-hover,
.container-team-name-text {
  @apply transition-all ease-in-out duration-200;
}

.team-card-container:hover div.clip-diagonal-right {
  @apply w-[140px];
  clip-path: polygon(0 0, 100% 0%, 38% 100%, 0% 100%);
}

/*.team-card-container:hover .clip-diagonal-right::after {
  @apply bottom-[105%] h-4;
}

.team-card-container:hover div.clip-diagonal-left {
  @apply w-[83px] translate-x-0;
  clip-path: polygon(80% 0, 100% 0%, 100% 100%, 0% 100%);
}

.team-card-container:hover .gradient-team-color-hover {
  @apply translate-x-0;
}*/

.team-card-container:hover .container-logo-img img,
.team-card-container:hover .container-logo-img div,
.team-card-container:hover .container-president-img img {
  @apply scale-110;
}

/*.team-card-container:hover .container-team-name-text {
  @apply ml-1;
}*/
</style>
